import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { isLimitError } from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { useProducts } from '../product/ProductContext';
import './memory.css';

const DESCRIPTIONS = {
    'strategy.md': 'What works for your account. Updated by the AI after every review.',
    'learnings.md': 'A log of every weekly review: targets vs actual results and what was learned.',
};

// Same rule as the server: letters, numbers and dashes, folders with /, ending in .md or .json
const VALID_PATH = /^[a-z0-9-]+(\/[a-z0-9-]+)*\.(md|json)$/i;

const describeFile = (path) => {
    if (DESCRIPTIONS[path]) return DESCRIPTIONS[path];
    if (path.startsWith('plans/')) return "A week's posts with their targets and actual results.";
    return 'Your note. The AI follows it when planning and reviewing.';
};

const fileIcon = (path) => {
    if (path.endsWith('.json')) return 'data_object';
    if (path.startsWith('plans/')) return 'event_note';
    if (path === 'learnings.md') return 'school';
    if (path === 'strategy.md') return 'flag';
    return 'description';
};

const formatWhen = (value) => {
    const date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatUpdated = (file) => {
    const who = file.updatedBy === 'user' ? 'Edited by you' : 'Updated by AI';
    const when = formatWhen(file.updatedAt);
    return when ? `${who} · ${when}` : who;
};

// "Brand voice" -> "brand-voice.md"; "ideas/Hooks" -> "ideas/hooks.md"
const toNotePath = (name) => {
    const base = name.trim().toLowerCase().replace(/\.md$/, '')
        .split('/')
        .map(part => part.replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''))
        .filter(Boolean)
        .join('/');
    return base ? `${base}.md` : '';
};

const normalizeFiles = (list) => (Array.isArray(list)
    ? list.filter(file => file && typeof file.path === 'string' && file.path)
    : []);

const normalizeFile = (raw, fallbackPath) => ({
    path: typeof raw?.path === 'string' && raw.path ? raw.path : fallbackPath,
    content: typeof raw?.content === 'string' ? raw.content : '',
    updatedBy: raw?.updatedBy,
    updatedAt: raw?.updatedAt,
});

// Root files first, then one group per folder (plans/...)
const groupFiles = (files) => {
    const groups = [];
    files.forEach(file => {
        const slash = file.path.lastIndexOf('/');
        const folder = slash === -1 ? '' : file.path.slice(0, slash);
        let group = groups.find(item => item.folder === folder);
        if (!group) {
            group = { folder, files: [] };
            groups.push(group);
        }
        group.files.push({ ...file, name: file.path.slice(slash + 1) });
    });
    return groups.sort((a, b) => (a.folder === '' ? -1 : b.folder === '' ? 1 : a.folder.localeCompare(b.folder)));
};

const folderLabel = (folder) => (folder === 'plans' ? 'Weekly plans' : folder);

const ConfirmDialog = ({ title, children, confirmLabel, isBusy, error, onConfirm, onCancel }) => {
    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && !isBusy && onCancel();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isBusy, onCancel]);

    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !isBusy && onCancel()}>
            <div
                className="ls-modal ls-memory-dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="ls-memory-dialog-title"
                aria-describedby="ls-memory-dialog-body"
            >
                <h2 id="ls-memory-dialog-title" className="ls-memory-dialog-title">{title}</h2>
                <div id="ls-memory-dialog-body" className="ls-memory-dialog-body">{children}</div>
                {error && <Alert severity="error">{error}</Alert>}
                <div className="ls-memory-dialog-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={onCancel} disabled={isBusy} autoFocus>
                        Cancel
                    </button>
                    <button type="button" className="ls-btn ls-memory-danger" onClick={onConfirm} disabled={isBusy}>
                        {isBusy && <span className="ls-spinner" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SKELETON_ROWS = [0, 1, 2, 3];

const MemoryScreen = () => {
    const navigate = useNavigate();
    const { currentProduct } = useProducts();
    const [files, setFiles] = useState([]);
    const [selected, setSelected] = useState(null); // { path, content, updatedBy, updatedAt }
    const [draft, setDraft] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [openingPath, setOpeningPath] = useState(null);
    const [openError, setOpenError] = useState(null); // { path, message }
    const [isSaving, setIsSaving] = useState(false);
    const [editorError, setEditorError] = useState('');
    const [savedAt, setSavedAt] = useState(null);
    const [isNaming, setIsNaming] = useState(false);
    const [noteName, setNoteName] = useState('');
    const [noteError, setNoteError] = useState('');
    const [isCreatingNote, setIsCreatingNote] = useState(false);
    const [confirm, setConfirm] = useState(null); // { title, body, confirmLabel, run }
    const [isConfirming, setIsConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState('');
    const latestPath = useRef(null);
    const noteInputRef = useRef(null);
    const textareaRef = useRef(null);
    const editorRef = useRef(null);

    const loadFiles = useCallback(async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            setFiles(normalizeFiles(await api.listMemoryFiles()));
        } catch (e) {
            setLoadError("Couldn't load the memory files. Check your connection and try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFiles();
    }, [loadFiles]);

    const isDirty = Boolean(selected) && draft !== selected.content;

    // Closing the tab with unsaved edits asks first
    useEffect(() => {
        if (!isDirty) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [isDirty]);

    const groups = useMemo(() => groupFiles(files), [files]);
    const notePath = toNotePath(noteName);

    const showFile = (file) => {
        setSelected(file);
        setDraft(file.content);
        setEditorError('');
        setSavedAt(null);
    };

    const loadFile = async (path) => {
        latestPath.current = path;
        setOpeningPath(path);
        setOpenError(null);
        try {
            const file = await api.getMemoryFile(path);
            if (latestPath.current !== path) return;
            showFile(normalizeFile(file, path));
            // On one-column layouts the editor is below the list
            if (window.matchMedia?.('(max-width: 999px)').matches) {
                editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } catch (e) {
            if (latestPath.current !== path) return;
            setOpenError({ path, message: `Couldn't open ${path}. Try again, or pick another file.` });
        } finally {
            if (latestPath.current === path) setOpeningPath(null);
        }
    };

    // Runs an action, asking first if it would throw away unsaved edits
    const guardDirty = (action) => {
        if (!isDirty) {
            action();
            return;
        }
        setConfirm({
            title: 'Discard unsaved changes?',
            body: <>Your edits to <strong>{selected.path}</strong> haven't been saved and will be lost.</>,
            confirmLabel: 'Discard changes',
            run: action,
        });
    };

    const openFile = (path) => {
        if (path === selected?.path && !openError) return;
        guardDirty(() => loadFile(path));
    };

    const closeConfirm = useCallback(() => {
        setConfirm(null);
        setConfirmError('');
    }, []);

    const runConfirm = async () => {
        setIsConfirming(true);
        setConfirmError('');
        try {
            await confirm.run();
            closeConfirm();
        } catch (e) {
            setConfirmError(e.message);
        } finally {
            setIsConfirming(false);
        }
    };

    const handleSave = async () => {
        if (!selected || isSaving) return;
        if (selected.path.endsWith('.json')) {
            try {
                JSON.parse(draft);
            } catch (e) {
                setEditorError(`This isn't valid JSON yet (${e.message}). Fix it, then save.`);
                return;
            }
        }
        const sentDraft = draft;
        setIsSaving(true);
        setEditorError('');
        try {
            const file = normalizeFile(await api.saveMemoryFile(selected.path, sentDraft), selected.path);
            const saved = { ...file, content: file.content || sentDraft };
            setSelected(saved);
            // Keep anything typed while the save was in flight
            setDraft(current => (current === sentDraft ? saved.content : current));
            setSavedAt(Date.now());
            loadFiles();
        } catch (e) {
            if (!isLimitError(e)) setEditorError(e.response?.data?.error || "Couldn't save the file. Your changes are still here; try again.");
        } finally {
            setIsSaving(false);
        }
    };

    // Ctrl/Cmd+S saves the open file from anywhere on the page, instead of the browser's "Save page"
    const saveShortcut = useRef(null);
    saveShortcut.current = () => {
        if (isDirty && !confirm) handleSave();
    };
    const hasFile = Boolean(selected);
    useEffect(() => {
        if (!hasFile) return undefined;
        const handleKey = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
                event.preventDefault();
                saveShortcut.current();
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [hasFile]);

    const startNote = () => {
        setIsNaming(true);
        setNoteError('');
        setTimeout(() => noteInputRef.current?.focus(), 0);
    };

    const cancelNote = () => {
        setIsNaming(false);
        setNoteName('');
        setNoteError('');
    };

    const createNote = async () => {
        setIsCreatingNote(true);
        setNoteError('');
        try {
            const file = normalizeFile(await api.saveMemoryFile(notePath, `# ${noteName.trim()}\n\n`), notePath);
            latestPath.current = file.path;
            setOpeningPath(null);
            setOpenError(null);
            showFile(file);
            cancelNote();
            loadFiles();
            // Ready to type under the heading
            setTimeout(() => {
                const textarea = textareaRef.current;
                if (!textarea) return;
                textarea.focus();
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }, 0);
        } catch (e) {
            if (!isLimitError(e)) setNoteError(e.response?.data?.error || "Couldn't create the note. Please try again.");
        } finally {
            setIsCreatingNote(false);
        }
    };

    const submitNote = (event) => {
        event.preventDefault();
        if (isCreatingNote) return;
        if (!noteName.trim()) {
            setNoteError('Give the note a name, e.g. brand-rules.');
            return;
        }
        if (!notePath || !VALID_PATH.test(notePath)) {
            setNoteError('Use letters or numbers in the name, e.g. brand-rules.');
            return;
        }
        if (files.some(file => file.path === notePath)) {
            setNoteError(`${notePath} already exists. Pick another name, or open it from the list.`);
            return;
        }
        guardDirty(createNote);
    };

    const askDelete = () => {
        const { path } = selected;
        setConfirm({
            title: `Delete ${path}?`,
            body: isDirty
                ? <>The AI will no longer use it, and your unsaved edits are lost too. This can't be undone.</>
                : <>The AI will no longer use it when it plans or reviews. This can't be undone.</>,
            confirmLabel: 'Delete file',
            run: async () => {
                try {
                    await api.deleteMemoryFile(path);
                } catch (e) {
                    throw new Error("Couldn't delete the file. Please try again.");
                }
                setFiles(current => current.filter(file => file.path !== path));
                setSelected(null);
                setDraft('');
                setSavedAt(null);
                loadFiles();
            },
        });
    };

    const handleRevert = () => {
        setDraft(selected.content);
        setEditorError('');
    };

    const renderEditor = () => {
        if (openingPath) {
            return (
                <div className="ls-memory-editor-body" aria-busy="true" aria-label={`Opening ${openingPath}`}>
                    <div className="ls-memory-editor-head">
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <span className="ls-skeleton-text" style={{ width: 180, height: 14 }} />
                            <span className="ls-skeleton-text" style={{ width: '70%' }} />
                        </div>
                    </div>
                    <div className="ls-skeleton ls-memory-textarea-skeleton" />
                </div>
            );
        }
        if (!selected) {
            return (
                <>
                    {openError && <Alert severity="error">{openError.message}</Alert>}
                    <div className="ls-placeholder ls-memory-placeholder">
                        <Icon name="psychology" />
                        <div className="ls-memory-placeholder-text">
                            The AI keeps its content strategy, weekly plans and learnings here, and updates them after every
                            plan and review.{' '}
                            {files.length > 0
                                ? 'Pick a file to read or edit it; your changes are used the next time it plans.'
                                : 'Anything you write here is used the next time it plans.'}
                        </div>
                    </div>
                </>
            );
        }
        return (
            <div className="ls-memory-editor-body ls-fade-in" key={selected.path}>
                <div className="ls-memory-editor-head">
                    <div className="ls-memory-editor-title">
                        <h2 className="ls-card-title ls-memory-path" id="ls-memory-editing">{selected.path}</h2>
                        <div className="ls-card-sub">{describeFile(selected.path)}</div>
                        <div className="ls-row-sub" style={{ marginTop: 2 }}>{formatUpdated(selected)}</div>
                    </div>
                    <div className="ls-actions ls-memory-editor-actions">
                        <button type="button" className="ls-btn ls-btn-icon" onClick={askDelete} aria-label={`Delete ${selected.path}`} title="Delete file">
                            <Icon name="delete" size={18} />
                        </button>
                        <button type="button" className="ls-btn ls-btn-secondary" onClick={handleRevert} disabled={!isDirty || isSaving}>
                            Revert
                        </button>
                        <button type="button" className="ls-btn ls-btn-primary" onClick={handleSave} disabled={!isDirty || isSaving}>
                            {isSaving ? <span className="ls-spinner" /> : <Icon name="check" size={18} />}
                            Save
                        </button>
                    </div>
                </div>
                {openError && <Alert severity="error" onClose={() => setOpenError(null)}>{openError.message}</Alert>}
                <textarea
                    ref={textareaRef}
                    className="ls-textarea ls-memory-textarea"
                    value={draft}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        setSavedAt(null);
                        setEditorError('');
                    }}
                    spellCheck={selected.path.endsWith('.md')}
                    aria-labelledby="ls-memory-editing"
                />
                {editorError && <Alert severity="error">{editorError}</Alert>}
                <div className="ls-memory-status" aria-live="polite">
                    {isDirty ? (
                        <><span className="ls-memory-dot" aria-hidden="true" />Unsaved changes · Ctrl+S to save</>
                    ) : savedAt ? (
                        <span className="ls-memory-saved"><Icon name="check_circle" size={16} />Saved. The AI will use this the next time it plans or reviews.</span>
                    ) : (
                        'No changes'
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Product{currentProduct?.name ? ` · ${currentProduct.name}` : ''}</div>
                    <h1 className="ls-page-title">Memory</h1>
                </div>
                <div className="ls-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={startNote} disabled={isNaming}>
                        <Icon name="note_add" size={18} />New note
                    </button>
                </div>
            </div>

            <div className="ls-memory-layout">
                <div className="ls-card ls-memory-files">
                    <div className="ls-card-header">
                        <span className="ls-card-title">Files</span>
                        {files.length > 0 && <span className="ls-card-sub">{files.length}</span>}
                    </div>

                    {isNaming && (
                        <form className="ls-memory-note-form ls-enter" onSubmit={submitNote} noValidate>
                            <label className="ls-field">
                                <span className="ls-label">Note name</span>
                                <input
                                    ref={noteInputRef}
                                    className="ls-input is-compact"
                                    value={noteName}
                                    onChange={(e) => {
                                        setNoteName(e.target.value);
                                        setNoteError('');
                                    }}
                                    onKeyDown={(e) => e.key === 'Escape' && cancelNote()}
                                    placeholder="e.g. brand-rules"
                                    aria-invalid={noteError ? true : undefined}
                                    aria-describedby="ls-memory-note-hint"
                                    disabled={isCreatingNote}
                                />
                                <span id="ls-memory-note-hint" className="ls-hint">
                                    {notePath ? <>Saved as <code>{notePath}</code></> : 'Rules or facts the AI should always follow.'}
                                </span>
                            </label>
                            {noteError && <Alert severity="error">{noteError}</Alert>}
                            <div className="ls-memory-note-actions">
                                <button type="button" className="ls-btn ls-btn-ghost" onClick={cancelNote} disabled={isCreatingNote}>Cancel</button>
                                <button type="submit" className="ls-btn ls-btn-secondary" disabled={isCreatingNote}>
                                    {isCreatingNote ? <span className="ls-spinner" /> : <Icon name="add" size={18} />}
                                    Create note
                                </button>
                            </div>
                        </form>
                    )}

                    {loadError && (
                        <Alert
                            severity="error"
                            action={(
                                <button type="button" className="ls-btn ls-btn-secondary ls-memory-alert-btn" onClick={loadFiles} disabled={isLoading}>
                                    {isLoading && <span className="ls-spinner" />}Try again
                                </button>
                            )}
                        >
                            {loadError}
                        </Alert>
                    )}

                    {isLoading && files.length === 0 ? (
                        <div className="ls-memory-list" aria-hidden="true">
                            {SKELETON_ROWS.map(i => <div key={i} className="ls-skeleton ls-memory-file-skeleton" />)}
                        </div>
                    ) : files.length === 0 ? (
                        !loadError && (
                            <div className="ls-memory-empty">
                                <p className="ls-empty" style={{ margin: 0 }}>
                                    Nothing here yet. The AI starts its memory with your first weekly plan: a strategy, each week's
                                    posts with targets, and what it learned from the results. Add your own notes too, like rules for
                                    your brand.
                                </p>
                                <button type="button" className="ls-btn ls-btn-secondary" onClick={() => navigate('/dashboard/plan')}>
                                    <Icon name="auto_awesome" size={18} />Plan this week
                                </button>
                            </div>
                        )
                    ) : (
                        <nav className="ls-memory-list" aria-label="Memory files">
                            {groups.map(group => (
                                <div key={group.folder || 'root'} className="ls-memory-group">
                                    {group.folder && <div className="ls-memory-folder">{folderLabel(group.folder)}</div>}
                                    <div className="ls-stagger ls-memory-group-files">
                                        {group.files.map((file, index) => {
                                            const isSelected = (openingPath || selected?.path) === file.path;
                                            return (
                                                <button
                                                    type="button"
                                                    key={file.path}
                                                    className={`ls-memory-file${isSelected ? ' is-selected' : ''}`}
                                                    style={{ '--i': index }}
                                                    onClick={() => openFile(file.path)}
                                                    aria-current={isSelected ? 'true' : undefined}
                                                    title={file.path}
                                                >
                                                    <Icon name={fileIcon(file.path)} size={18} />
                                                    <span style={{ flex: 1, minWidth: 0 }}>
                                                        <span className="ls-row-title">{file.name}</span>
                                                        <span className="ls-row-sub">{formatUpdated(file)}</span>
                                                    </span>
                                                    {openingPath === file.path ? (
                                                        <span className="ls-spinner" />
                                                    ) : isSelected && isDirty ? (
                                                        <span className="ls-memory-dot" title="Unsaved changes" />
                                                    ) : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </nav>
                    )}
                </div>

                <div className="ls-card ls-memory-editor" ref={editorRef}>
                    {renderEditor()}
                    {confirm && (
                        <ConfirmDialog
                            title={confirm.title}
                            confirmLabel={confirm.confirmLabel}
                            isBusy={isConfirming}
                            error={confirmError}
                            onConfirm={runConfirm}
                            onCancel={closeConfirm}
                        >
                            {confirm.body}
                        </ConfirmDialog>
                    )}
                </div>
            </div>
        </>
    );
};

export default MemoryScreen;
